import { EnumPlans } from '@/enums/Plans';
import { ISession } from '@/types/Session';
import { translate } from '@/helpers/translation.helper';
import EnumDocumentStatus from '@/enums/DocumentStatus';
import Segment from '@lib-shared/segment';
import { ESegmentUTMParameter } from '@lib-shared/types/segment';

export function cacheBuster(force: boolean) {
  let cachebuster: string;
  if (force) {
    cachebuster = `${Math.round(new Date().getTime() / 1000)}`;
  } else {
    cachebuster = 'init';
  }

  return cachebuster;
}

export function getUTMParameterByName(name: ESegmentUTMParameter): string {
  const query = window.location.search.toString();
  const utmName = name.replace(/[[]/, '\\[').replace(/[\]]/, '\\]');
  const regexS = `[\\?&]${utmName}=([^&#]*)`;
  const regex = new RegExp(regexS);
  const results = regex.exec(query);
  if (results == null) {
    return;
  }
  return decodeURIComponent(results[1].replace(/\+/g, ' '));
}

export function getUTMData(): Partial<Record<ESegmentUTMParameter, string>> {
  if (document.cookie) {
    const hsfirstvisit = getHubspotCookie();

    const utmData = {
      hsfirstvisit,
    };

    Object.values(ESegmentUTMParameter).forEach(
      (param: ESegmentUTMParameter) => (utmData[param] = getUTMParameterByName(param))
    );

    return utmData;
  }
}

export function getHubspotCookie(): string {
  if (document.cookie) {
    let pairs: any = /(?:^|; )hsfirstvisit=([^;\\|]*)/.exec(document.cookie) || [];
    if (pairs) {
      pairs = pairs.slice(1).pop();
      const hsfirstvisit = decodeURIComponent(pairs).substring(0, 100);
      if (hsfirstvisit !== 'undefined') {
        return hsfirstvisit;
      }
    }
  }
}

export const capitalize = (text: string): string =>
  text ? text[0].toUpperCase() + text.slice(1) : '';

export const getBooleanForBackend = (value: boolean): string => {
  return value ? 'true' : '';
};

export const getDocumentUrl = (id: string, status: string): string => {
  return ['draft', 'importing'].indexOf(status) > -1
    ? `/document/edit/${id}`
    : `/document/view/${id}`;
};

export const sanitizeEmail = (email: string): string => {
  return email.toLowerCase().replace('å', 'a').replace('ä', 'a').replace('ö', 'o');
};

export const jsonToParams = (json: object, separator = '&') => {
  return Object.keys(json)
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(json[key])}`)
    .join(separator);
};

export const uniqueArray = (value: string[]): string[] => {
  const set = new Set(value);
  return Array.from(set);
};

export const fullName = (firstName: string, lastName): string => {
  return `${firstName} ${lastName}`.trim();
};

export const GA_BETA_OPT_IN = 'ga_beta_opt_in';

export function isWindows(nav: Navigator): boolean {
  return nav.userAgent.indexOf('Windows') != -1;
}

export function getDocumentStatusClass(documentStatus: string) {
  switch (documentStatus) {
    case 'draft':
      return 'ga-default';

    case 'hardbounced':
    case 'partlyrejected':
    case 'rejected':
    case 'recalled':
    case 'lost':
    case 'expired':
      return 'ga-danger';

    case 'deferred':
    case 'reviewed':
    case 'viewed':
    case 'commented':
    case 'clicked':
    case 'downloaded':
    case 'attachment':
    case 'attachmentopened':
    case 'waitingforsignature':
    case 'partlysigned':
      return 'ga-warning';

    case 'sent':
    case 'sms':
    case 'reminder':
    case 'video':
    case 'paused':
    case 'updated':
      return 'ga-primary';

    case 'processing':
    case 'importing':
    case 'printed':
    case 'created':
    case 'sealed':
    case 'scheduled':
    case 'waiting':
    case 'archived':
      return 'ga-info';

    case 'delivered':
    case 'approved':
    case 'verified':
    case 'signed':
      return 'ga-success';

    case 'signedwithoutverification':
      return 'ga-success unverified';

    default:
      return 'ga-default';
  }
}

export function inProduction(currentWindow: Window): boolean {
  return currentWindow.location.href.includes('getaccept.com');
}

export function isPaidPlan(session: ISession): boolean {
  switch (session.plan) {
    // case EnumPlans.trial: We have decided that trial plan is something that is manually enabled from our sales department. They need to be able to access paid stuff such as branding and teams.
    case EnumPlans.free:
    case EnumPlans.solo:
      return false;
    default:
      return true;
  }
}

export function getDocumentStatusIconName(status: EnumDocumentStatus): string {
  switch (status) {
    case EnumDocumentStatus.draft:
      return 'document-status/draft';
    case EnumDocumentStatus.hardbounced:
      return 'document-status/hardbounced';
    case EnumDocumentStatus.importing:
      return 'document-status/importing';
    case EnumDocumentStatus.lost:
      return 'document-status/lost';
    case EnumDocumentStatus.processing:
      return 'document-status/processing';
    case EnumDocumentStatus.recalled:
      return 'document-status/deleted';
    case EnumDocumentStatus.rejected:
      return 'document-status/rejected';
    case EnumDocumentStatus.reviewed:
      return 'document-status/reviewed';
    case EnumDocumentStatus.scheduled:
      return 'document-status/scheduled';
    case EnumDocumentStatus.sealed:
      return 'document-status/sealed';
    case EnumDocumentStatus.sent:
      return 'document-status/sent';
    case EnumDocumentStatus.signed:
      return 'document-status/signed';
    case EnumDocumentStatus.signedwithoutverification:
      return 'document-status/signed-without-verification';
    case EnumDocumentStatus.viewed:
      return 'document-status/viewed';
    default:
      return 'document-status/sent';
  }
}

export function getBinaryPayloadValue(isSet: boolean): string {
  return isSet ? '1' : '0';
}

export function isLegacyPlan(plan: EnumPlans): boolean {
  switch (plan) {
    case EnumPlans.trial:
    case EnumPlans.solo:
    case EnumPlans.starter:
    case EnumPlans.business:
    case EnumPlans.pro:
    case EnumPlans.enterprise:
      return true;
    default:
      return false;
  }
}

export function numToWord(num: number): string {
  switch (num) {
    case 1:
      return translate('one');
    case 2:
      return translate('two');
    case 3:
      return translate('three');
    case 4:
      return translate('four');
    case 5:
      return translate('five');
    case 6:
      return translate('six');
    case 7:
      return translate('seven');
    case 8:
      return translate('eight');
    case 9:
      return translate('nine');
    case 10:
      return translate('ten');
    default:
      return String(num);
  }
}

export function getDocumentStatusTranslation(status: EnumDocumentStatus): string {
  if (status === EnumDocumentStatus.recalled) {
    return capitalize(translate('deleted'));
  }
  return translate(`document-status-${status}`);
}

export function logOut(): void {
  Segment.reset();
  localStorage.removeItem('GA_SESSION');
  window.location.href = '/logout';
}

export function getTemplateIdFromRegistrationCampaign(registrationCampaign: string): string {
  const pattern = 'templateid_';
  const patternIndex = registrationCampaign.indexOf(pattern) + pattern.length;
  return registrationCampaign.substring(
    patternIndex,
    registrationCampaign.indexOf('|', patternIndex) === -1
      ? registrationCampaign.length
      : registrationCampaign.indexOf('|', patternIndex)
  );
}

export function hasValidString(value: string): boolean {
  return !!value && value !== 'undefined' && value !== 'null' && value !== '';
}
